import { Query, JsonApiRequestError, Mutation } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import { or } from 'patronum';
import { accountTreeModel } from 'entities/account';
import {
	USER_NAME_MAX_LENGTH,
	USER_NAME_MIN_LENGTH,
	USER_NOTE_MAX_LENGTH,
	USER_PASSWORD_MIN_LENGTH,
} from 'entities/user';
import {
	AddUserParams,
	AddUserResponse,
	createAddUserMutation,
	createGetAccountsQuery,
	EntityWithName,
	USER_ROLES,
	UserRole,
} from 'shared/api';
import {
	createModalFormModel,
	maxLengthRule,
	minLengthRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type AddUserFormValue = {
	name: string;
	password: string;
	accountId: number | null;
	role: UserRole | null;
	note: string;
	hasMultiLogin: boolean;
};

export type AddUserModel = ModalFormModel<null, AddUserFormValue> & {
	addUserMutation: Mutation<AddUserParams, AddUserResponse, JsonApiRequestError>;
	getAccountsQuery: Query<void, EntityWithName[], JsonApiRequestError>;
	$accounts: Store<EntityWithName[]>;
	$pending: Store<boolean>;
	availableRoles: UserRole[];
};

export const createAddUserModel = (): AddUserModel => {
	const addUserMutation = createAddUserMutation();

	const getAccountsQuery = createGetAccountsQuery();

	const model = createModalFormModel<null, AddUserFormValue>({
		fields: {
			name: {
				init: '',
				rules: [
					notEmptyRule,
					minLengthRule(USER_NAME_MIN_LENGTH),
					maxLengthRule(USER_NAME_MAX_LENGTH),
				],
			},
			password: {
				init: '',
				rules: [notEmptyRule, minLengthRule(USER_PASSWORD_MIN_LENGTH)],
			},
			accountId: {
				init: null,
				rules: [notEmptyRule],
			},
			role: {
				init: null,
				rules: [notEmptyRule],
			},
			note: {
				init: '',
				rules: [maxLengthRule(USER_NOTE_MAX_LENGTH)],
			},
			hasMultiLogin: {
				init: false,
			},
		},
		validateOn: ['submit'],
		resetErrorsOnChange: true,
	});

	const $accounts = getAccountsQuery.$data.map((result) => result ?? []);

	const $pending = or(getAccountsQuery.$pending, addUserMutation.$pending);

	const availableRoles = [USER_ROLES.admin, USER_ROLES.cashier];

	sample({
		clock: model.modalButtonClicked,
		target: [model.form.fields.accountId.disable, getAccountsQuery.start],
	});

	sample({
		clock: model.modalButtonClicked,
		source: accountTreeModel.$singleSelectedNode,
		filter: (accountId): accountId is number => accountId !== null,
		target: model.form.fields.accountId.setValue,
	});

	chain(getAccountsQuery.finished.success, model.form.fields.accountId.enable);

	sample({
		clock: model.form.submitted,
		source: model.form.$values,
		fn: (values) => ({
			name: values.name,
			password: values.password,
			accountId: values.accountId!,
			role: values.role!,
			note: values.note ?? undefined,
			hasMultiLogin: values.hasMultiLogin,
		}),
		target: addUserMutation.start,
	});

	chain(addUserMutation.finished.success, model.switcher.disable);

	notify(addUserMutation.finished.success, 'add-user.success');

	sample({
		clock: model.modalClosed,
		target: [getAccountsQuery.reset, addUserMutation.reset],
	});

	return {
		getAccountsQuery,
		addUserMutation,
		$accounts,
		$pending,
		availableRoles,
		...model,
	};
};
